import { SystemFeaturesContext, Tooltip, useNotification, UserContext } from '@/components';
import { ScrapbookContentType, ScrapbookSaveItem } from '@/types';
import { Ref, useContext, useState } from 'react';
import ScissorIcon from '@/assets/scissors-light.svg';
import { FolderSelectionDialog } from '.';
import { saveBulkToScrapbook, saveToScrapbook } from '@/api';

export interface SaveToScrapbookButtonMethods {
  open: () => void;
}

type SaveToScrapbookButtonProps = {
  scrapbookPath: string;
  scrapbookContentType: ScrapbookContentType;
  scrapbookContent: any;
  successMessage?: string;
  tooltipProps?: Omit<React.ComponentProps<typeof Tooltip>, 'children'>;
  children?: React.ReactNode;
  bulkContentGenerator?: () => ScrapbookSaveItem[];
  disabled?: boolean;
  ref?: Ref<SaveToScrapbookButtonMethods>;
} & React.ButtonHTMLAttributes<HTMLButtonElement>;

const SaveToScrapbookButton = ({
  scrapbookPath,
  scrapbookContentType,
  scrapbookContent,
  successMessage,
  tooltipProps,
  children,

  bulkContentGenerator,
  disabled: disabledProp,
  ref,
  ...buttonProps
}: SaveToScrapbookButtonProps) => {
  const [showFolderDialog, setShowFolderDialog] = useState(false);

  const user = useContext(UserContext);
  const { showNotification } = useNotification();
  const { saveToScrapbook: saveToScrapbookFeature } = useContext(SystemFeaturesContext);

  const onScrapbookFolderSelected = async (selectedFolderId: number) => {
    if (bulkContentGenerator) {
      const contentList = bulkContentGenerator();
      saveBulkToScrapbook(selectedFolderId, contentList, user?.userId).then(({ succeeded, message }) => {
        if (succeeded) {
          showNotification(successMessage || 'Save to scrapbook successfully', 'success');
        } else {
          showNotification(`Failed to save to scrapbook: ${message}`, 'error');
        }
      });
    } else {
      const linkObject: ScrapbookSaveItem = {
        path: scrapbookPath,
        contentType: scrapbookContentType,
        content: JSON.stringify(scrapbookContent),
      };

      saveToScrapbook(selectedFolderId, linkObject, user?.userId).then(({ succeeded, message }) => {
        if (succeeded) {
          showNotification(successMessage || 'Save to scrapbook successfully', 'success');
        } else {
          showNotification(`Failed to save to scrapbook: ${message}`, 'error');
        }
      });
    }
    setShowFolderDialog(false);
  };

  if (ref && typeof ref === 'object') {
    ref.current = { open: () => setShowFolderDialog(true) };
  }

  const disabled = disabledProp || !user || user?.isIpBasedIdentity || !saveToScrapbookFeature;

  return (
    <>
      <Tooltip
        placement="bottom"
        {...tooltipProps}
        backgroundColor={disabled ? 'orange' : tooltipProps?.backgroundColor}
        content={
          disabled ? 'Save to scrapbook: available for Members only' : tooltipProps?.content || 'Save to Scrapbook'
        }
      >
        <button
          onClick={() => setShowFolderDialog(true)}
          className="btn icon"
          title={tooltipProps?.content || 'Save to Scrapbook'}
          disabled={disabled}
          {...buttonProps}
        >
          {children || <ScissorIcon className="h-6 w-6" />}
        </button>
      </Tooltip>
      <FolderSelectionDialog
        isOpen={showFolderDialog}
        onClose={() => setShowFolderDialog(false)}
        onSave={onScrapbookFolderSelected}
      />
    </>
  );
};

export default SaveToScrapbookButton;
